import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, Truck, Cpu } from "lucide-react";
import { MatchedLine } from "@/components/MatchBadge";

// PICK THE RECORD FROM A SEARCH — his rule: a person never types a VIN to place a file.
export const TargetPicker = ({ open, kind, onClose, onPick }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setQ(""); setItems([]); } }, [open, kind]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get("/bulk-upload/targets", { params: { kind, q } }); setItems(data.items || []); setTotal(data.total || 0); }
      catch { setItems([]); setTotal(0); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, kind, open]);

  const Icon = kind === "part" ? Cpu : Truck;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="target-picker" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-base">{kind === "part" ? "Pick the engine part" : "Pick the truck"}</DialogTitle>
          <DialogDescription>
            {kind === "part" ? "Search by tag, engine serial (ESN) or name." : "Search by stock number, the last digits of the VIN, year / make / model or PO."}
          </DialogDescription>
        </DialogHeader>
        <Input data-testid="target-picker-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={kind === "part" ? "e.g. FAS-001234 or 79943914" : "e.g. DNR-00179 or 268360"} className="h-11 rounded-lg" />
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-200">
          {busy && <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-400" /></div>}
          {!busy && q.trim().length >= 2 && items.length === 0 && (
            <div data-testid="target-picker-empty" className="p-3 text-sm text-slate-500">Nothing matches that.</div>
          )}
          {items.map((it, i) => (
            <button key={it.id} type="button" data-testid={`target-picker-item-${i}`} onClick={() => onPick({ kind, ...it })}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 transition-colors">
              <Icon className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800">{it.label}</span>
                {it.sub && <span className="block text-[11px] font-mono text-slate-500 truncate">{it.sub}</span>}
                {it.match && <MatchedLine match={it.match} testid={`target-picker-matched-${i}`} />}
              </span>
            </button>
          ))}
        </div>
        {!busy && total > items.length && (
          <p data-testid="target-picker-more" className="text-[12px] text-amber-800">
            Showing {items.length} of {total} — type more of the stock number, VIN or name to narrow it down.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
